import React, { useMemo, useState } from 'react';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import ImagesByRoom from './ImagesByRoom';
import LogoVideoSection from './LogoVideoSection';
import MainImageLibrary from './MainImageLibrary';

interface Props {
  images?: Array<App.OfferImage>;
  propertyId?: string;
  propertyLogoId?: string;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

export interface MediaSourceImage {
  id?: string;
  id_cloudinary_external?: string;
  id_image?: number | string;
  title?: string | null;
}

export type PropertyRoomTypeWithImages = App.RoomType & {
  images?: Array<MediaSourceImage>;
};

export interface MediaImage {
  publicId: string;
  sourceLabel: string;
  title?: string;
}

const UNASSIGNED_IMAGE_LABEL = 'Unassigned';

function getSourceImages({
  images,
  seenPublicIds,
  sourceLabel,
}: {
  images?: Array<MediaSourceImage>;
  seenPublicIds: Set<string>;
  sourceLabel: string;
}): Array<MediaImage> {
  return (images ?? []).reduce<Array<MediaImage>>((mediaImages, image) => {
    if (!image.id_cloudinary_external || seenPublicIds.has(image.id_cloudinary_external)) {
      return mediaImages;
    }

    seenPublicIds.add(image.id_cloudinary_external);
    mediaImages.push({
      publicId: image.id_cloudinary_external,
      sourceLabel,
      title: image.title || undefined,
    });

    return mediaImages;
  }, []);
}

function buildMediaImages({
  offerImages,
  propertyImages,
  propertyRoomTypes,
}: {
  offerImages: Array<App.OfferImage>;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}) {
  const seenPublicIds = new Set<string>();
  const heroImages = [offerImages, propertyImages].flatMap((images) =>
    getSourceImages({
      images,
      seenPublicIds,
      sourceLabel: 'Property hero',
    }),
  );
  const roomImages = (propertyRoomTypes ?? []).flatMap((room) =>
    getSourceImages({
      images: room.images,
      seenPublicIds,
      sourceLabel: room.name ? `${room.name}` : 'Room',
    }),
  );

  return heroImages.concat(roomImages);
}

function getPrimaryVideoId(videos?: Props['videos']) {
  const luxuryEscapesVideoId = videos?.luxuryescapes?.vimeo_id;

  return luxuryEscapesVideoId && luxuryEscapesVideoId !== 'undefined' ? luxuryEscapesVideoId : undefined;
}

function togglePublicId(publicIds: Array<string>, publicId: string) {
  return publicIds.includes(publicId)
    ? publicIds.filter((currentPublicId) => currentPublicId !== publicId)
    : publicIds.concat(publicId);
}

export default function ImagesVideo({
  images = [],
  propertyId,
  propertyLogoId,
  propertyImages,
  propertyRoomTypes,
  vendorId,
  videos,
}: Props) {
  const [deletedImagePublicIds, setDeletedImagePublicIds] = useState<Array<string>>([]);
  const [hiddenImagePublicIds, setHiddenImagePublicIds] = useState<Array<string>>([]);
  const [imageSourceLabelOverrides, setImageSourceLabelOverrides] = useState<Record<string, string>>({});
  const labelledMediaImages = useMemo(() => {
    const deletedImagePublicIdSet = new Set(deletedImagePublicIds);

    return buildMediaImages({
      offerImages: images,
      propertyImages,
      propertyRoomTypes,
    })
      .filter((image) => !deletedImagePublicIdSet.has(image.publicId))
      .map((image) => ({
        ...image,
        sourceLabel: imageSourceLabelOverrides[image.publicId] ?? image.sourceLabel,
      }));
  }, [deletedImagePublicIds, imageSourceLabelOverrides, images, propertyImages, propertyRoomTypes]);

  const handleAssignImagesToRoom = (roomName: string, selectedImages: Array<MediaImage>) => {
    setImageSourceLabelOverrides((currentLabels) => {
      const nextLabels = { ...currentLabels };

      selectedImages.forEach((image) => {
        nextLabels[image.publicId] = roomName;
      });

      return nextLabels;
    });
  };

  const handleRemoveImageFromRoom = (publicId: string) => {
    setImageSourceLabelOverrides((currentLabels) => ({
      ...currentLabels,
      [publicId]: UNASSIGNED_IMAGE_LABEL,
    }));
  };

  const handleDeleteImage = (publicId: string) => {
    setDeletedImagePublicIds((currentPublicIds) =>
      currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
    );
    setHiddenImagePublicIds((currentPublicIds) =>
      currentPublicIds.filter((currentPublicId) => currentPublicId !== publicId),
    );
    setImageSourceLabelOverrides((currentLabels) => {
      const nextLabels = { ...currentLabels };

      delete nextLabels[publicId];

      return nextLabels;
    });
  };

  const handleToggleHiddenImage = (publicId: string) => {
    setHiddenImagePublicIds((currentPublicIds) => togglePublicId(currentPublicIds, publicId));
  };

  return (
    <Box>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4">Images & Video</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage offer media from one place. Upload property images, organise them by room type, choose the property
          logo, and attach video content.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 4 }}>
        <Button variant="outlined" startIcon={<DriveFolderUploadOutlinedIcon />}>
          Upload folder
        </Button>
        <Button variant="contained" startIcon={<AddPhotoAlternateOutlinedIcon />}>
          Add image
        </Button>
      </Stack>

      <MainImageLibrary
        hiddenImagePublicIds={hiddenImagePublicIds}
        mediaImages={labelledMediaImages}
        onDeleteImage={handleDeleteImage}
        onToggleHiddenImage={handleToggleHiddenImage}
      />

      <ImagesByRoom
        excludedImagePublicIds={deletedImagePublicIds}
        hiddenImagePublicIds={hiddenImagePublicIds}
        mediaImages={labelledMediaImages}
        onAssignImagesToRoom={handleAssignImagesToRoom}
        onRemoveImageFromRoom={handleRemoveImageFromRoom}
        onToggleHiddenImage={handleToggleHiddenImage}
        propertyRoomTypes={propertyRoomTypes}
      />
      <LogoVideoSection
        propertyId={propertyId}
        propertyLogoId={propertyLogoId}
        vendorId={vendorId}
        videoId={getPrimaryVideoId(videos)}
        videoPosterImage={labelledMediaImages[0]}
      />
    </Box>
  );
}
